import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { ArrowRight, CheckCircle, Phone, Video, MessageSquare, Cloud } from 'lucide-react'
import { SEO } from '@/lib/seo'
import { Button, Badge, AnimatedCard, CardHeader, CardTitle, CardDescription } from '@/components/ui'
import { CTASection } from '@/components/sections'
import { getServiceById } from '@/data/services'
import { companyInfo } from '@/data/company'

export function UCaaS() {
  const service = getServiceById('ucaas')!

  return (
    <>
      <SEO
        title="UCaaS - Unified Communications as a Service"
        description="Transform your business communications with enterprise-grade voice, video, messaging, and collaboration tools. Zoom Certified installer and support specialist."
      />

      {/* Hero Section */}
      <section className="section-padding relative overflow-hidden">
        <div className="absolute inset-0 bg-hero-pattern opacity-50" />
        
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Badge variant="glow" className="mb-6">UCaaS Solutions</Badge>
              
              <h1 className="text-display-md md:text-display-lg lg:text-display-xl font-heading font-bold text-white mb-6">
                {service.title}
              </h1>

              <p className="text-lg text-slate-300 mb-8">
                {service.longDescription}
              </p>

              {/* Zoom Certification Badges */}
              <div className="flex flex-wrap gap-4 mb-8 items-center">
                {service.certifications?.map((cert) => (
                  <div
                    key={cert}
                    className="flex items-center gap-3 glass-card px-4 py-3 rounded-xl"
                  >
                    <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                      <svg viewBox="0 0 32 32" className="w-8 h-8" fill="none">
                        <rect width="32" height="32" rx="6" fill="#2D8CFF"/>
                        <path d="M8 10.5C8 9.67 8.67 9 9.5 9H15.5C16.33 9 17 9.67 17 10.5V21.5C17 22.33 16.33 23 15.5 23H9.5C8.67 23 8 22.33 8 21.5V10.5Z" fill="white"/>
                        <path d="M18 13L24 9.5V22.5L18 19V13Z" fill="white"/>
                      </svg>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-white">{cert}</div>
                      <div className="text-xs text-slate-400">Verified Partner</div>
                    </div>
                  </div>
                ))}
                {/* Zoom Up Partner Badge */}
                <a
                  href="https://partner.zoom.us/partner-locator/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:scale-105 transition-transform glass-card rounded-xl p-2"
                >
                  <img 
                    src="/zoom-up-partner-badge.png" 
                    alt="Zoom Up Partner Program - Strategic Advisor" 
                    className="h-20 w-auto"
                  />
                </a>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button size="lg" variant="glow" className="group">
                    Get UCaaS Quote
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <a href={`tel:${companyInfo.phoneRaw}`}>
                  <Button size="lg" variant="secondary">
                    <Phone className="w-5 h-5" />
                    {companyInfo.phone}
                  </Button>
                </a>
              </div>
            </motion.div>

            {/* Visual */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="hidden lg:block"
            >
              <div className="glass-card p-8 rounded-3xl">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { icon: Video, label: 'HD Video', desc: 'Crystal clear video conferencing' },
                    { icon: Phone, label: 'Voice', desc: 'Enterprise voice solutions' },
                    { icon: MessageSquare, label: 'Messaging', desc: 'Team chat & collaboration' },
                    { icon: Cloud, label: 'Cloud PBX', desc: 'Modern phone system' },
                  ].map((item, index) => (
                    <motion.div
                      key={item.label}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 + index * 0.1, duration: 0.5 }}
                      className="glass-card-hover p-6"
                    >
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent-500 to-electric-500 flex items-center justify-center mb-4 shadow-glow">
                        <item.icon className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="font-semibold text-white mb-1">{item.label}</h3>
                      <p className="text-sm text-slate-400">{item.desc}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-primary-600/30">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <Badge className="mb-4">Features</Badge>
            <h2 className="text-display-sm md:text-display-md font-heading font-bold text-white mb-4">
              Everything You Need to Communicate
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Our UCaaS solutions bring together all the communication tools your team needs in one unified platform.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {service.features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <AnimatedCard className="h-full">
                  <CardHeader>
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-accent-500/20 to-electric-500/20 flex items-center justify-center mb-4">
                      <feature.icon className="w-7 h-7 text-accent-400" />
                    </div>
                    <CardTitle>{feature.title}</CardTitle>
                    <CardDescription className="text-base">
                      {feature.description}
                    </CardDescription>
                  </CardHeader>
                </AnimatedCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Badge className="mb-4">Benefits</Badge>
              <h2 className="text-display-sm md:text-display-md font-heading font-bold text-white mb-6">
                Why Choose Our UCaaS Solutions?
              </h2>
              <p className="text-lg text-slate-400 mb-8">
                Experience the difference of working with Zoom Certified experts who understand your business communication needs.
              </p>

              <div className="space-y-4">
                {service.benefits.map((benefit, index) => (
                  <motion.div
                    key={benefit}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex items-center gap-4"
                  >
                    <div className="w-8 h-8 rounded-full bg-success-500/20 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-success-500" />
                    </div>
                    <span className="text-slate-300">{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="glass-card p-8 rounded-3xl"
            >
              <div className="flex items-start justify-between mb-6">
                <h3 className="text-xl font-heading font-semibold text-white">
                  Zoom Certified Excellence
                </h3>
                <a
                  href="https://partner.zoom.us/partner-locator/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:scale-105 transition-transform glass-card rounded-xl p-2"
                >
                  <img 
                    src="/zoom-up-partner-badge.png" 
                    alt="Zoom Up Partner Program - Strategic Advisor" 
                    className="h-28 w-auto"
                  />
                </a>
              </div>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                    <svg viewBox="0 0 32 32" className="w-8 h-8" fill="none">
                      <rect width="32" height="32" rx="6" fill="#2D8CFF"/>
                      <path d="M8 10.5C8 9.67 8.67 9 9.5 9H15.5C16.33 9 17 9.67 17 10.5V21.5C17 22.33 16.33 23 15.5 23H9.5C8.67 23 8 22.33 8 21.5V10.5Z" fill="white"/>
                      <path d="M18 13L24 9.5V22.5L18 19V13Z" fill="white"/>
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-2">Deployment: Phone</h4>
                    <p className="text-sm text-slate-400">
                      Certified by Zoom to deploy and configure Zoom Phone for enterprise voice communications.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                    <svg viewBox="0 0 32 32" className="w-8 h-8" fill="none">
                      <rect width="32" height="32" rx="6" fill="#2D8CFF"/>
                      <path d="M8 10.5C8 9.67 8.67 9 9.5 9H15.5C16.33 9 17 9.67 17 10.5V21.5C17 22.33 16.33 23 15.5 23H9.5C8.67 23 8 22.33 8 21.5V10.5Z" fill="white"/>
                      <path d="M18 13L24 9.5V22.5L18 19V13Z" fill="white"/>
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-2">Support: Workplace & Phone</h4>
                    <p className="text-sm text-slate-400">
                      Certified support specialists providing ongoing technical support and optimization for your entire Zoom deployment.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <CTASection />
    </>
  )
}

